import { ChevronDown } from "lucide-react";
import { SHOW_MORE_STEP } from "../../lib/pagination";

/**
 * The load-more control for accumulating lists (paired with useShowMore) — the
 * counterpart to PaginationFooter's prev/next paging. Renders nothing when the
 * whole list is already visible, so short lists stay clutter-free.
 *
 * The label announces what THIS click reveals — the next batch, i.e.
 * min(step, remaining) — not the full hidden remainder, so it never
 * over-promises (a click loads one step, not everything left). The visible text
 * is the button's accessible name, so screen readers hear the same count.
 */
export function ShowMoreButton({
  hasMore,
  remaining,
  onClick,
  step = SHOW_MORE_STEP,
  itemNoun = "item",
}) {
  if (!hasMore) return null;

  const safeRemaining = Math.max(
    0,
    Number.isFinite(remaining) ? Math.floor(remaining) : 0
  );
  const safeStep =
    Number.isFinite(step) && step > 0 ? Math.floor(step) : safeRemaining;
  const count = Math.min(safeStep, safeRemaining);
  const noun = count === 1 ? itemNoun : `${itemNoun}s`;

  return (
    <div className="mt-4 flex justify-center">
      <button
        type="button"
        onClick={onClick}
        className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        <ChevronDown className="h-4 w-4" />
        Show {count} more {noun}
      </button>
    </div>
  );
}
